"use client";

import * as React from "react";
import Link from "next/link";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { CalendarIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";

import { cn } from "@/lib/utils";
import { WEBSITE_RE } from "@/lib/website-format";
import { api } from "@/trpc/react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

/* ────────────────────────────────────────────
   Schema
──────────────────────────────────────────── */

const leadSchema = z.object({
  // Step 1
  firstName: z.string().min(1, "Required"),
  lastName: z.string().min(1, "Required"),
  // Pipe preserves the empty-vs-malformed distinction in error messages:
  // empty input → "Required"; non-empty but malformed → "Invalid email address".
  // A flat `z.email("Invalid email address")` would say "Invalid…" on empty too.
  email: z.string().min(1, "Required").pipe(z.email("Invalid email address")),
  phone: z.string().min(1, "Required"),
  // Step 2
  businessType: z.string().min(1, "Required"),
  industry: z.string().min(1, "Required"),
  revenueRange: z.string().min(1, "Required"),
  aiExperience: z.string().min(1, "Required"),
  // Step 3
  painPoints: z.array(z.string()).min(1, "Select at least one"),
  specificNeeds: z.string().max(5000),
  // O2 (plan §4.1) — optional, step 2. `businessWebsite` IS in STEP_FIELDS
  // (below) despite being optional, so a malformed value is caught by
  // "Next step" while the field is still visible — the multi-step form
  // otherwise validates the WHOLE schema only at final submit, which would
  // block submission with an error on a step the user can no longer see.
  businessWebsite: z
    .string()
    .trim()
    .max(300)
    .refine((v) => v === "" || WEBSITE_RE.test(v), {
      message: "Enter a valid website, like example.com or https://example.com",
    }),
  socialHandle: z.string().trim().max(120),
  servicesInterested: z.array(z.string()),
  // Step 4
  prefDay: z.date({ error: "Pick a date" }),
  prefTime: z.string().min(1, "Required"),
  timezone: z.string().min(1, "Required"),
  // Only meaningful when `timezone` is "Other" (see timezoneOtherError).
  timezoneOther: z.string().trim().max(80, "Keep it under 80 characters"),
  hearAbout: z.string().min(1, "Required"),
});

type LeadFormValues = z.infer<typeof leadSchema>;

const STEP_FIELDS: Record<number, (keyof LeadFormValues)[]> = {
  1: ["firstName", "lastName", "email", "phone"],
  // `businessWebsite` is optional but validated HERE (not left to final
  // submit) so a format error shows on step 2, where the field lives — see
  // the schema's comment above (review P1-2).
  2: ["businessType", "industry", "revenueRange", "aiExperience", "businessWebsite"],
  3: ["painPoints"],
  4: ["prefDay", "prefTime", "timezone", "timezoneOther", "hearAbout"],
};

/* ────────────────────────────────────────────
   Constants
──────────────────────────────────────────── */

const BUSINESS_TYPES = [
  "Small Business",
  "Solo Entrepreneur",
  "Content Creator",
  "Professional Services",
  "E-commerce",
  "Personal Use",
];

const AI_EXPERIENCE = ["Never", "A little", "Regularly"];

const PAIN_POINTS = [
  "Email & Follow-ups",
  "Social Media",
  "Lead Generation",
  "Customer Support",
  "Reporting & Admin",
  "CRM Management",
  "Scheduling",
  "Other",
];

const TIME_SLOTS = [
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
];

// O2 (plan §4.1) — same service keys as convex/clientTasks.ts's
// SERVICE_KEYS / backend/app/routers/admin_clients.py's Engagement model.
const SERVICES_OF_INTEREST: { key: string; label: string }[] = [
  { key: "ai_integration", label: "AI / Automation" },
  { key: "website_development", label: "Website Development" },
  { key: "seo_marketing", label: "SEO & Marketing" },
  { key: "social_media", label: "Social Media" },
  { key: "other", label: "Something else" },
];

const REVENUE_RANGES = [
  "Under $5K",
  "$5K - $25K",
  "$25K - $100K",
  "$100K - $500K",
  "$500K+",
  "Prefer not to say",
];

const TIMEZONES = [
  "Eastern (ET)",
  "Central (CT)",
  "Mountain (MT)",
  "Pacific (PT)",
  "Other",
];

const OTHER_TIMEZONE = "Other";

/** Required text for the "Other" timezone choice; null when valid / not needed. */
export function timezoneOtherError(timezone: string, other: string): string | null {
  if (timezone !== OTHER_TIMEZONE) return null;
  return other.trim() === "" ? "Enter your timezone, like Europe/London or GMT+1" : null;
}

/** The timezone to save: the typed value instead of the literal "Other". */
export function resolveTimezone(timezone: string, other: string): string {
  return timezone === OTHER_TIMEZONE ? other.trim() : timezone;
}

const REFERRAL_SOURCES = [
  "Instagram",
  "TikTok",
  "LinkedIn",
  "Google Search",
  "YouTube",
  "Facebook",
  "Referral / Word of Mouth",
  "Other",
];

const TOTAL_STEPS = 4;

/* ────────────────────────────────────────────
   Shared styling constants
──────────────────────────────────────────── */

const inputStyles =
  "h-auto rounded-xl my-1 border-border bg-secondary px-4 py-3.5 text-[0.9375rem] focus-visible:border-primary focus-visible:ring-0";

const labelStyles =
  "text-[0.8125rem] font-semibold uppercase tracking-wider text-muted-foreground";

const selectTriggerStyles =
  "h-auto w-full rounded-xl border-border bg-secondary px-4 py-3.5 text-[0.9375rem] focus-visible:border-primary focus-visible:ring-0 data-placeholder:text-muted-foreground my-1";

/* ────────────────────────────────────────────
   Shared sub-components
──────────────────────────────────────────── */

function OptionCard({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "cursor-pointer rounded-xl border-2 px-4 py-3.5 text-center text-sm font-semibold transition-all",
        selected
          ? "border-primary bg-primary/8 text-primary"
          : "border-border bg-secondary text-muted-foreground hover:border-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function ProgressBar({
  currentStep,
  totalSteps,
}: {
  currentStep: number;
  totalSteps: number;
}) {
  return (
    <div className="flex gap-1.5 px-1">
      {Array.from({ length: totalSteps }, (_, i) => {
        const step = i + 1;
        let state: "done" | "active" | "pending" = "pending";
        if (step < currentStep || currentStep > totalSteps) state = "done";
        else if (step === currentStep) state = "active";

        return (
          <div
            key={step}
            className={cn(
              "h-1 flex-1 rounded-full transition-colors duration-400",
              state === "done" && "bg-primary/60",
              state === "active" && "bg-primary",
              state === "pending" && "bg-secondary",
            )}
          />
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────
   Step components
──────────────────────────────────────────── */

type StepProps = {
  form: ReturnType<typeof useForm<LeadFormValues>>;
};

function StepAboutYou({ form }: StepProps) {
  const {
    register,
    formState: { errors },
  } = form;

  return (
    <>
      <h3 className="text-foreground mb-1 text-xl font-bold">
        Tell us about yourself
      </h3>
      <p className="text-muted-foreground mb-6 text-sm">
        We&apos;ll use this to personalize your demo experience.
      </p>

      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
        <div className="mb-4">
          <Label className={labelStyles}>First Name *</Label>
          <Input
            className={inputStyles}
            placeholder="John"
            aria-invalid={!!errors.firstName}
            {...register("firstName")}
          />
          <FieldError errors={[errors.firstName]} />
        </div>
        <div className="mb-4">
          <Label className={labelStyles}>Last Name *</Label>
          <Input
            className={inputStyles}
            placeholder="Smith"
            aria-invalid={!!errors.lastName}
            {...register("lastName")}
          />
          <FieldError errors={[errors.lastName]} />
        </div>
      </div>

      <div className="mb-4">
        <Label className={labelStyles}>Email *</Label>
        <Input
          className={inputStyles}
          type="email"
          placeholder="john@company.com"
          aria-invalid={!!errors.email}
          {...register("email")}
        />
        <FieldError errors={[errors.email]} />
      </div>

      <div className="mb-4">
        <Label className={labelStyles}>Phone *</Label>
        <Input
          className={inputStyles}
          type="tel"
          placeholder="+1 (555) 000-0000"
          aria-invalid={!!errors.phone}
          {...register("phone")}
        />
        <p className="text-muted-foreground mt-1 text-xs">
          So we can call you at your chosen slot.
        </p>
        <FieldError errors={[errors.phone]} />
      </div>
    </>
  );
}

function StepBusiness({ form }: StepProps) {
  const {
    register,
    control,
    formState: { errors },
  } = form;

  return (
    <>
      <h3 className="text-foreground mb-1 text-xl font-bold">
        What best describes you?
      </h3>
      <p className="text-muted-foreground mb-6 text-sm">
        This helps us recommend the right AI setup for you.
      </p>

      <Controller
        name="businessType"
        control={control}
        render={({ field }) => (
          <div className="mb-4">
            <div className="mb-1 grid grid-cols-2 gap-2.5">
              {BUSINESS_TYPES.map((type) => (
                <OptionCard
                  key={type}
                  selected={field.value === type}
                  onClick={() => field.onChange(type)}
                >
                  {type}
                </OptionCard>
              ))}
            </div>
            <FieldError errors={[errors.businessType]} />
          </div>
        )}
      />

      <div className="mb-4">
        <Label className={labelStyles}>What industry are you in?</Label>
        <Input
          className={inputStyles}
          placeholder="e.g. Real estate, Law, Marketing, Fitness..."
          aria-invalid={!!errors.industry}
          {...register("industry")}
        />
        <FieldError errors={[errors.industry]} />
      </div>

      <Controller
        name="revenueRange"
        control={control}
        render={({ field }) => (
          <div className="mb-4">
            <Label className={labelStyles}>Monthly Revenue Range</Label>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger
                className={selectTriggerStyles}
                aria-invalid={!!errors.revenueRange}
              >
                <SelectValue placeholder="Select range..." />
              </SelectTrigger>
              <SelectContent>
                {REVENUE_RANGES.map((range) => (
                  <SelectItem key={range} value={range}>
                    {range}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError errors={[errors.revenueRange]} />
          </div>
        )}
      />

      <Controller
        name="aiExperience"
        control={control}
        render={({ field }) => (
          <div className="mb-4">
            <Label className={labelStyles}>
              Have you used AI tools before?
            </Label>
            <div className="mb-1 grid grid-cols-3 gap-2">
              {AI_EXPERIENCE.map((level) => (
                <OptionCard
                  key={level}
                  selected={field.value === level}
                  onClick={() => field.onChange(level)}
                >
                  {level}
                </OptionCard>
              ))}
            </div>
            <FieldError errors={[errors.aiExperience]} />
          </div>
        )}
      />

      <div className="mb-4">
        <Label className={labelStyles}>Website (optional)</Label>
        <Input
          className={inputStyles}
          placeholder="https://example.com"
          aria-invalid={!!errors.businessWebsite}
          {...register("businessWebsite")}
        />
        <FieldError errors={[errors.businessWebsite]} />
      </div>

      <div className="mb-4">
        <Label className={labelStyles}>Instagram or other social (optional)</Label>
        <Input
          className={inputStyles}
          placeholder="@yourbusiness"
          {...register("socialHandle")}
        />
      </div>

      <Controller
        name="servicesInterested"
        control={control}
        render={({ field }) => {
          const toggle = (key: string) => {
            const current = field.value ?? [];
            const next = current.includes(key)
              ? current.filter((k) => k !== key)
              : [...current, key];
            field.onChange(next);
          };
          return (
            <div className="mb-4">
              <Label className={labelStyles}>
                Services you&apos;re interested in (optional)
              </Label>
              <div className="mb-1 grid grid-cols-2 gap-2.5">
                {SERVICES_OF_INTEREST.map((s) => (
                  <OptionCard
                    key={s.key}
                    selected={(field.value ?? []).includes(s.key)}
                    onClick={() => toggle(s.key)}
                  >
                    {s.label}
                  </OptionCard>
                ))}
              </div>
            </div>
          );
        }}
      />
    </>
  );
}

function StepPainPoints({ form }: StepProps) {
  const {
    register,
    control,
    formState: { errors },
  } = form;

  return (
    <>
      <h3 className="text-foreground mb-1 text-xl font-bold">
        What do you need help with?
      </h3>
      <p className="text-muted-foreground mb-6 text-sm">
        Select all that apply. Our AI agents can handle all of these.
      </p>

      <Controller
        name="painPoints"
        control={control}
        render={({ field }) => {
          const toggle = (point: string) => {
            const current = field.value ?? [];
            const next = current.includes(point)
              ? current.filter((p) => p !== point)
              : [...current, point];
            field.onChange(next);
          };

          return (
            <div className="mb-4">
              <div className="mb-1 grid grid-cols-2 gap-2.5">
                {PAIN_POINTS.map((point) => (
                  <OptionCard
                    key={point}
                    selected={(field.value ?? []).includes(point)}
                    onClick={() => toggle(point)}
                  >
                    {point}
                  </OptionCard>
                ))}
              </div>
              <FieldError errors={[errors.painPoints]} />
            </div>
          );
        }}
      />

      <div className="mb-4">
        <Label className={labelStyles}>
          Anything specific you want to automate?
        </Label>
        <Textarea
          className={cn(inputStyles, "h-[90px] resize-none")}
          placeholder="Tell us what takes up most of your time..."
          maxLength={5000}
          {...register("specificNeeds")}
        />
      </div>
    </>
  );
}

function StepSchedule({ form }: StepProps) {
  const {
    control,
    register,
    formState: { errors },
  } = form;

  return (
    <>
      <h3 className="text-foreground mb-1 text-xl font-bold">
        When works for a quick call?
      </h3>
      <p className="text-muted-foreground mb-6 text-sm">
        Pick a time and we&apos;ll reach out to confirm.
      </p>

      <Controller
        name="prefDay"
        control={control}
        render={({ field }) => (
          <div className="mb-4">
            <Label className={labelStyles}>Preferred Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "border-border bg-secondary focus:border-primary mb-1 flex w-full items-center justify-between rounded-xl border px-4 py-3.5 text-left text-[0.9375rem] transition-colors outline-none",
                    field.value ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {field.value
                    ? format(field.value, "EEEE, MMMM d, yyyy")
                    : "Pick a date..."}
                  <CalendarIcon className="text-muted-foreground size-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={field.value}
                  onSelect={field.onChange}
                  disabled={(date) => {
                    const day = date.getDay();
                    const tomorrow = new Date();
                    tomorrow.setHours(0, 0, 0, 0);
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    return day === 0 || day === 6 || date < tomorrow;
                  }}
                />
              </PopoverContent>
            </Popover>
            <FieldError errors={[errors.prefDay]} />
          </div>
        )}
      />

      <Controller
        name="prefTime"
        control={control}
        render={({ field }) => (
          <div className="mb-4">
            <Label className={labelStyles}>Preferred Time</Label>
            <div className="mb-1 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {TIME_SLOTS.map((time) => (
                <OptionCard
                  key={time}
                  selected={field.value === time}
                  onClick={() => field.onChange(time)}
                >
                  {time}
                </OptionCard>
              ))}
            </div>
            <FieldError errors={[errors.prefTime]} />
          </div>
        )}
      />

      <Controller
        name="timezone"
        control={control}
        render={({ field }) => (
          <div className="mb-4">
            <Label className={labelStyles}>Timezone</Label>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger
                className={selectTriggerStyles}
                aria-invalid={!!errors.timezone}
              >
                <SelectValue placeholder="Select timezone..." />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError errors={[errors.timezone]} />
            {field.value === OTHER_TIMEZONE && (
              <div className="mt-3">
                <Input
                  className={inputStyles}
                  placeholder="Your timezone, e.g. Europe/London"
                  aria-label="Your timezone"
                  aria-invalid={!!errors.timezoneOther}
                  {...register("timezoneOther")}
                />
                <FieldError errors={[errors.timezoneOther]} />
              </div>
            )}
          </div>
        )}
      />

      <Controller
        name="hearAbout"
        control={control}
        render={({ field }) => (
          <div className="mb-4">
            <Label className={labelStyles}>How did you hear about us?</Label>
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger
                className={selectTriggerStyles}
                aria-invalid={!!errors.hearAbout}
              >
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {REFERRAL_SOURCES.map((source) => (
                  <SelectItem key={source} value={source}>
                    {source}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError errors={[errors.hearAbout]} />
          </div>
        )}
      />
    </>
  );
}

function StepThankYou({ onClose }: { onClose: () => void }) {
  return (
    <div className="py-5 text-center">
      <m.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
          delay: 0.1,
        }}
        className="border-primary bg-primary/10 mx-auto mb-6 flex size-20 items-center justify-center rounded-full border-[3px]"
      >
        <Check className="text-primary size-9" />
      </m.div>
      <h2 className="text-foreground mb-2 text-2xl font-bold">
        You&apos;re all set!
      </h2>
      <p className="text-muted-foreground mx-auto max-w-xs text-sm leading-relaxed">
        Thanks for your interest in Raileon. Our team will reach out within 24
        hours to confirm your demo time.
        <br />
        <br />
        We&apos;re excited to show you what AI can do for your business.
      </p>
      <Button onClick={onClose} className="mt-6 px-10 py-3">
        Close
      </Button>
    </div>
  );
}

/**
 * Review P1-2: distinguishes a genuine server-side input-validation failure
 * (the tRPC error shape carries `data.zodError` — see
 * `server/api/trpc.ts`'s `errorFormatter`) from every other failure
 * (network blip, Turnstile re-check, rate limit, …). Duck-typed rather than
 * `instanceof TRPCClientError` so this file doesn't need the full
 * `@trpc/client` error type plumbing for one boolean check.
 */
function isZodValidationError(err: unknown): boolean {
  if (typeof err !== "object" || err === null) return false;
  const data = (err as { data?: { zodError?: unknown } }).data;
  return Boolean(data?.zodError);
}

export const SUBMIT_FAILED_MESSAGE =
  "We couldn't submit your request right now \u2014 please email admin@raileon.com and we'll get right back to you.";

export function isHoneypotFilled(value: string): boolean {
  return value.length > 0;
}

/** What to tell the visitor when the submit mutation throws. */
export function submitFailure(err: unknown): {
  kind: "validation" | "server";
  message: string;
  description?: string;
} {
  if (isZodValidationError(err)) {
    return {
      kind: "validation",
      message: "Please check the highlighted field and try again.",
      description: "Your info wasn't lost \u2014 fix it and resubmit.",
    };
  }
  // A throttle rejection already carries the same sentence from the server.
  return { kind: "server", message: SUBMIT_FAILED_MESSAGE };
}

/* ────────────────────────────────────────────
   Main form component
──────────────────────────────────────────── */

interface LeadCaptureFormProps {
  onClose: () => void;
}

/**
 * A Turnstile token is single use: after a failed submit it is spent, so
 * drop it and re-challenge. The submit button then waits for a fresh token.
 */
export function resetTurnstile(
  widgetRef: { current: Pick<TurnstileInstance, "reset"> | null },
  clearToken: () => void,
) {
  clearToken();
  widgetRef.current?.reset();
}

export function LeadCaptureForm({ onClose }: LeadCaptureFormProps) {
  const [step, setStep] = React.useState(1);
  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(
    null,
  );
  const turnstileRef = React.useRef<TurnstileInstance | null>(null);
  const submitLead = api.leads.submit.useMutation();

  const form = useForm<LeadFormValues>({
    resolver: zodResolver(leadSchema),
    mode: "onTouched",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      businessType: "",
      industry: "",
      revenueRange: "",
      aiExperience: "",
      painPoints: [],
      specificNeeds: "",
      businessWebsite: "",
      socialHandle: "",
      servicesInterested: [],
      prefDay: undefined,
      prefTime: "",
      timezone: "",
      timezoneOther: "",
      hearAbout: "",
    },
  });

  const handleNext = async () => {
    const fields = STEP_FIELDS[step];
    if (!fields) return;
    const valid = await form.trigger(fields);
    if (valid) setStep((s) => s + 1);
  };

  const onSubmit = async (values: LeadFormValues) => {
    // Step 4 is the last step, so "validated on that step" happens here.
    const tzMessage = timezoneOtherError(values.timezone, values.timezoneOther);
    if (tzMessage) {
      form.setError("timezoneOther", { type: "required", message: tzMessage });
      return;
    }
    if (!turnstileToken) return;
    const honeypot =
      (document.getElementById("website") as HTMLInputElement)?.value ?? "";
    // Bots fill the hidden field: give them a silent fake success and send
    // nothing. This is the ONLY path that shows the thank-you without a save.
    if (isHoneypotFilled(honeypot)) {
      setStep(5);
      return;
    }
    const { timezoneOther, ...rest } = values;
    try {
      await submitLead.mutateAsync({
        ...rest,
        timezone: resolveTimezone(values.timezone, timezoneOther),
        prefDay: format(values.prefDay, "yyyy-MM-dd"),
        specificNeeds: values.specificNeeds || undefined,
        businessWebsite: values.businessWebsite || undefined,
        socialHandle: values.socialHandle || undefined,
        servicesInterested:
          values.servicesInterested.length > 0
            ? values.servicesInterested
            : undefined,
        turnstileToken,
        website: honeypot,
      });
    } catch (err) {
      resetTurnstile(turnstileRef, () => setTurnstileToken(null));
      // Nothing was saved on ANY error here, so never show the thank-you:
      // a false success silently loses the lead. Input-validation errors ask
      // the visitor to fix the field; everything else (Convex down, network,
      // Turnstile, throttle) asks them to email us.
      const failure = submitFailure(err);
      toast.error(failure.message, failure.description ? { description: failure.description } : undefined);
      if (failure.kind === "server") {
        try {
          const Sentry = await import("@sentry/nextjs");
          // No form values (not even the email) go to Sentry.
          Sentry.captureException(err, {
            tags: { surface: "lead-capture-form" },
          });
        } catch {
          console.error("Lead submission failed:", err);
        }
      }
      return;
    }
    setStep(5);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep(1);
      form.reset();
    }, 300);
  };

  const isThankYou = step > TOTAL_STEPS;

  return (
    <LazyMotion features={domAnimation}>
    <div className="flex flex-col">
      {/* Header */}
      {!isThankYou && (
        <div className="mb-5">
          <div className="text-foreground mb-4 text-xl font-extrabold">
            raileon<span className="text-primary">.</span>
          </div>
          <ProgressBar currentStep={step} totalSteps={TOTAL_STEPS} />
        </div>
      )}

      {/* Steps */}
      <AnimatePresence mode="wait">
        <m.div
          key={step}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {step === 1 && <StepAboutYou form={form} />}
          {step === 2 && <StepBusiness form={form} />}
          {step === 3 && <StepPainPoints form={form} />}
          {step === 4 && <StepSchedule form={form} />}
          {isThankYou && <StepThankYou onClose={handleClose} />}
        </m.div>
      </AnimatePresence>

      {/* Honeypot — hidden from real users, bots auto-fill it */}
      <input
        id="website"
        name="website"
        type="text"
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        className="absolute -left-[9999px] size-0 opacity-0"
      />

      {/* Turnstile + Navigation */}
      {!isThankYou && (
        <>
          {step === TOTAL_STEPS && (
            <div className="mt-3 flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                options={{ theme: "dark", size: "flexible" }}
              />
            </div>
          )}
          {step === TOTAL_STEPS && (
            <p className="text-muted-foreground mt-3 text-center text-xs leading-relaxed">
              By submitting you agree we may contact you by email, phone or
              text about your request. Message and data rates may apply;
              reply STOP to opt out. See our{" "}
              <Link href="/privacy" className="text-primary underline">
                Privacy Policy
              </Link>
              .
            </p>
          )}
          <div className="mt-2 flex gap-3">
            {step > 1 && (
              <Button
                variant="outline"
                className="flex-1 py-3"
                onClick={() => setStep((s) => s - 1)}
              >
                Back
              </Button>
            )}
            {step < TOTAL_STEPS ? (
              <Button className="flex-1 py-3" onClick={handleNext}>
                Next step
              </Button>
            ) : (
              <Button
                className="flex-2 py-3"
                disabled={submitLead.isPending || !turnstileToken}
                onClick={(e) => form.handleSubmit(onSubmit)(e)}
              >
                {submitLead.isPending ? "Submitting..." : "Submit & Book Demo"}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
    </LazyMotion>
  );
}
